import { useState, useEffect, useRef, Fragment } from 'react';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  LinearProgress,
  Alert,
  Slider,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Badge,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  FormGroup,
  Checkbox,
  Radio,
  RadioGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AutoStories,
  TextFields,
  Image,
  Palette,
  MovieFilter,
  CheckCircle,
  Error as ErrorIcon,
  ExpandMore,
  ContentCopy,
  Delete,
  Edit,
  Save,
  Download,
  Upload,
  PlayArrow,
  Merge,
  CallSplit,
  Analytics,
  Visibility,
  ViewModule,
  ViewList,
  BrokenImage,
  Warning,
  TipsAndUpdates,
  Person,
  Landscape,
  Brush,
  Camera,
  Theaters,
  Add,
  Remove,
  CloudUpload,
  Link as LinkIcon,
  ContentPaste,
  LocationCity,
  Architecture,
  Science,
  Checkroom,
  Restaurant,
  MusicNote,
} from '@mui/icons-material';
import {
  StoryPromptSession,
  TextPartition,
  SectionPrompt,
  VisualStyleSettings,
  CharacterDescription,
  SettingDescription,
  PartitionStrategy,
  ArtStyle,
  ConsistencyLevel,
  ColorPaletteMode,
  AnalyzeTextRequest,
  GeneratePromptsRequest,
  ConsistencyCheckRequest,
  BatchIterateRequest,
} from '../models/storyPromptStudio';
import { storyPromptStudioService } from '../services/storyPromptStudioService';
import { ReactNode } from 'react';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`story-tabpanel-${index}`}
      aria-labelledby={`story-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AGENT_CATEGORIES = {
  'Core Visual': [
    { id: 'prompt-improvement', name: 'Prompt Improvement', icon: <Edit />, description: 'Refine and optimize prompts' },
    { id: 'image-prompt', name: 'Image Prompt Iterator', icon: <Image />, description: 'Generate and iterate visual descriptions' },
    { id: 'prompt-evolution', name: 'Prompt Evolution', icon: <TipsAndUpdates />, description: 'Multi-agent prompt refinement' },
  ],
  'Narrative Enhancement': [
    { id: 'character', name: 'Character Consistency', icon: <Person />, description: 'Ensure character consistency' },
    { id: 'scene', name: 'Scene Visionary', icon: <Landscape />, description: 'Create immersive scenes' },
    { id: 'storyboard', name: 'Storyboard Agent', icon: <Theaters />, description: 'Professional storyboarding' },
    { id: 'story-generator', name: 'Story Generator', icon: <AutoStories />, description: 'Enhance narrative elements' },
    { id: 'screenplay', name: 'Screenplay Generator', icon: <MovieFilter />, description: 'Cinematic structure' },
  ],
  'Creative Enhancement': [
    { id: 'abstract-thinker', name: 'Abstract Thinker', icon: <TipsAndUpdates />, description: 'Add conceptual depth' },
    { id: 'creative-studio', name: 'Creative Project', icon: <Palette />, description: 'Comprehensive creative workflow' },
    { id: 'comic-book', name: 'Comics Generator', icon: <Brush />, description: 'Comic book style narratives' },
  ],
  'Environmental & Setting': [
    { id: 'environmental-sim', name: 'Environmental Simulation', icon: <Landscape />, description: 'Natural environment details' },
    { id: 'urban-culture', name: 'Urban Culture', icon: <LocationCity />, description: 'Urban and street settings' },
    { id: 'architecture', name: 'Architecture Visualization', icon: <Architecture />, description: 'Architectural details' },
  ],
  'Specialized Domains': [
    { id: 'scientific', name: 'Scientific Explorer', icon: <Science />, description: 'Scientific accuracy' },
    { id: 'fashion', name: 'Fashion Design', icon: <Checkroom />, description: 'Fashion and clothing details' },
    { id: 'culinary', name: 'Culinary Experience', icon: <Restaurant />, description: 'Food and dining scenes' },
    { id: 'music', name: 'Music & Sound', icon: <MusicNote />, description: 'Audio environment descriptions' },
  ],
};

const ART_STYLES: Record<ArtStyle, string> = {
  'photorealistic': 'Photorealistic',
  'illustration': 'Illustration',
  'anime': 'Anime',
  'oil-painting': 'Oil Painting',
  'watercolor': 'Watercolor',
  'digital-art': 'Digital Art',
  'concept-art': 'Concept Art',
  'comic': 'Comic',
  'fantasy': 'Fantasy',
  'sci-fi': 'Sci-Fi',
};

export const StoryPromptStudio: React.FC = () => {
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Backend session retrieval
  const {
    sessions: backendSessions,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'StoryPromptStudio',
    sessionBased: true,
    autoFetch: true,
  });

  const [sessions, setSessions] = useState<StoryPromptSession[]>([]);
  const [currentSession, setCurrentSession] = useState<StoryPromptSession | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Input state
  const [inputMethod, setInputMethod] = useState<'paste' | 'file' | 'url'>('paste');
  const [inputText, setInputText] = useState('');
  const [inputUrl, setInputUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Partition state
  const [partitionStrategy, setPartitionStrategy] = useState<PartitionStrategy>('auto');
  const [targetPartitions, setTargetPartitions] = useState(5);
  const [maxPartitionSize, setMaxPartitionSize] = useState(500);
  const [detectElements, setDetectElements] = useState({
    characters: true,
    settings: true,
    emotions: true,
    timeChanges: true,
    visualCues: true,
  });

  // Visual style state
  const [visualStyle, setVisualStyle] = useState<VisualStyleSettings>({
    artStyle: 'illustration',
    colorPalette: 'mood-based',
    consistency: 'high',
    aspectRatio: '16:9',
    artisticInfluence: '',
    globalElements: [],  // Changed from globalModifiers to match API
    moodProgression: 'evolve',
    cinematicStyle: '',
  });

  // Agent selection state
  const [selectedAgents, setSelectedAgents] = useState<string[]>(['prompt-improvement', 'image-prompt']);
  
  // Iteration dialog state
  const [iterationDialogOpen, setIterationDialogOpen] = useState(false);
  const [selectedIterationType, setSelectedIterationType] = useState<string>('enhance');
  const [selectedIterationAgent, setSelectedIterationAgent] = useState<string>('prompt-improvement');
  const [iterationFeedback, setIterationFeedback] = useState<string>('');

  // View state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedPartitions, setSelectedPartitions] = useState<string[]>([]);

  // Load and merge sessions from localStorage and backend
  useEffect(() => {
    const localSessions = storyPromptStudioService.getAllSessions();

    if (backendSessions && backendSessions.length > 0) {
      const converted: StoryPromptSession[] = backendSessions.map(sessionWithAssets => {
        const session = sessionWithAssets.session;
        const sessionData = session.sessionData ? JSON.parse(session.sessionData) : null;

        return {
          id: session.id,
          title: session.title || sessionData?.title || 'Untitled Story',
          inputText: sessionData?.inputText || '',
          inputSource: sessionData?.inputSource || 'paste',
          partitions: sessionData?.partitions || [],
          characters: sessionData?.characters || [],
          settings: sessionData?.settings || [],
          visualStyle: sessionData?.visualStyle || {
            artStyle: 'illustration',
            colorPalette: 'mood-based',
            consistency: 'high',
            aspectRatio: '16:9',
            artisticInfluence: '',
            globalElements: [],
            moodProgression: 'evolve',
            cinematicStyle: '',
          },
          generatedPrompts: sessionData?.generatedPrompts || [],
          selectedAgents: sessionData?.selectedAgents || [],
          iterationMode: sessionData?.iterationMode || false,
          metadata: sessionData?.metadata || session.metadata || {
            totalWords: 0,
            totalPartitions: 0,
            averagePartitionSize: 0,
          },
          createdAt: session.createdAt,
          updatedAt: session.updatedAt || session.lastUpdatedAt || session.createdAt,
        };
      });

      // Merge: backend wins, add local-only sessions
      const backendIds = new Set(converted.map(s => s.id));
      const localOnly = localSessions.filter(s => !backendIds.has(s.id));
      setSessions([...converted, ...localOnly]);
    } else {
      setSessions(localSessions);
    }
  }, [backendSessions]);

  const loadSessions = () => {
    const savedSessions = storyPromptStudioService.getAllSessions();
    setSessions(savedSessions);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await storyPromptStudioService.processTextFile(file);
      setInputText(text);
    } catch (err) {
      setError('Failed to read file');
    }
  };

  const handleUrlFetch = async () => {
    if (!inputUrl) return;

    setIsProcessing(true);
    try {
      const text = await storyPromptStudioService.fetchTextFromUrl(inputUrl);
      setInputText(text);
    } catch (err) {
      setError('Failed to fetch content from URL');
    } finally {
      setIsProcessing(false);
    }
  };

  const createSession = async () => {
    if (!inputText) {
      setError('Please provide text to analyze');
      return;
    }

    const title = inputText.split('\n')[0].substring(0, 50) || 'Untitled Story';
    const newSession = storyPromptStudioService.createLocalSession(
      title,
      inputText,
      inputMethod
    );
    
    newSession.visualStyle = visualStyle;
    newSession.selectedAgents = selectedAgents;

    try {
      const response = await storyPromptStudioService.startSession({
        inputText,
        inputSource: inputMethod,
        visualStyle: {
          ...visualStyle,
          globalElements: visualStyle.globalElements || [],  // Ensure globalElements is always an array
        },
        selectedAgents,
        iterationMode: true,
      });

      newSession.id = response.id;
      storyPromptStudioService.saveSession(newSession);
      setCurrentSession(newSession);
      setSessions([...sessions, newSession]);
      setActiveStep(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    }
  };

  const analyzeAndPartition = async () => {
    if (!currentSession) return;

    setIsProcessing(true);
    setError(null);

    try {
      const request: AnalyzeTextRequest = {
        text: currentSession.inputText,
        partitionStrategy,
        targetPartitions,
        maxPartitionSize,
        detectElements,
      };

      const response = await storyPromptStudioService.analyzeText(request);

      const updatedSession = { ...currentSession };
      updatedSession.partitions = response.partitions;
      updatedSession.metadata.totalPartitions = response.partitions.length;
      updatedSession.metadata.averagePartitionSize = 
        response.partitions.reduce((sum, p) => sum + p.text.length, 0) / response.partitions.length;

      storyPromptStudioService.saveSession(updatedSession);
      setCurrentSession(updatedSession);
      setActiveStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze text');
    } finally {
      setIsProcessing(false);
    }
  };

  const generatePrompts = async () => {
    if (!currentSession || currentSession.partitions.length === 0) return;

    setIsProcessing(true);
    setError(null);

    try {
      const request: GeneratePromptsRequest = {
        partitions: currentSession.partitions,
        visualStyle: currentSession.visualStyle,
        previousPrompts: currentSession.generatedPrompts.map(p => p.imagePrompt),
      };

      const response = await storyPromptStudioService.generatePrompts(request);

      const updatedSession = { ...currentSession };
      updatedSession.generatedPrompts = response.prompts;

      storyPromptStudioService.saveSession(updatedSession);
      setCurrentSession(updatedSession);
      setActiveStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate prompts');
    } finally {
      setIsProcessing(false);
    }
  };

  const checkConsistency = async () => {
    if (!currentSession || currentSession.generatedPrompts.length === 0) return;

    setIsProcessing(true);
    setError(null);

    try {
      const request: ConsistencyCheckRequest = {
        prompts: currentSession.generatedPrompts,
        elements: {
          characters: currentSession.characters,
          settings: currentSession.settings,
          style: currentSession.visualStyle,
        },
      };

      const response = await storyPromptStudioService.checkConsistency(request);

      // Display consistency results in a dialog
      // For now, just show the score
      alert(`Consistency Score: ${response.consistencyScore}%\nIssues found: ${response.issues.length}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check consistency');
    } finally {
      setIsProcessing(false);
    }
  };

  const iteratePrompts = async (iterationType?: string, agentType?: string, feedback?: string) => {
    if (!currentSession || selectedPartitions.length === 0) return;

    setIsProcessing(true);
    setError(null);

    try {
      const selectedPrompts = currentSession.generatedPrompts
        .filter(p => selectedPartitions.includes(p.partitionId))
        .map(p => p.imagePrompt);

      const request: BatchIterateRequest = {
        sessionId: currentSession.id,
        prompts: selectedPrompts,
        iterationType: iterationType || selectedIterationType,
        agentType: agentType || selectedIterationAgent,
        globalFeedback: feedback || iterationFeedback || '',  // Use provided feedback or dialog feedback
      };

      const response = await storyPromptStudioService.iterateBatch(request);

      // Update prompts with iterations
      const updatedSession = { ...currentSession };
      response.iteratedPrompts.forEach((iterated, index) => {
        const promptIndex = updatedSession.generatedPrompts.findIndex(
          p => p.imagePrompt === iterated.originalPrompt
        );
        if (promptIndex !== -1) {
          updatedSession.generatedPrompts[promptIndex].imagePrompt = iterated.updatedPrompt;
        }
      });

      storyPromptStudioService.saveSession(updatedSession);
      setCurrentSession(updatedSession);
      setSelectedPartitions([]);
      setIterationDialogOpen(false);
      setIterationFeedback('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to iterate prompts');
    } finally {
      setIsProcessing(false);
    }
  };

  const mergePartitions = () => {
    if (!currentSession || selectedPartitions.length < 2) return;

    const indices = selectedPartitions
      .map(id => currentSession.partitions.findIndex(p => p.id === id))
      .filter(i => i !== -1);

    const mergedPartitions = storyPromptStudioService.mergePartitions(
      currentSession.partitions,
      indices
    );

    const updatedSession = { ...currentSession };
    updatedSession.partitions = mergedPartitions;
    updatedSession.metadata.totalPartitions = mergedPartitions.length;

    storyPromptStudioService.saveSession(updatedSession);
    setCurrentSession(updatedSession);
    setSelectedPartitions([]);
  };

  const exportSession = () => {
    if (!currentSession) return;
    
    const exportData = storyPromptStudioService.exportSession(currentSession);
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `story-prompt-${currentSession.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPromptsAsMarkdown = () => {
    if (!currentSession) return;
    
    const markdown = storyPromptStudioService.exportPromptsAsMarkdown(currentSession);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `story-prompts-${currentSession.id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportForImageGeneration = () => {
    if (!currentSession) return;
    
    const prompts = storyPromptStudioService.exportPromptsForImageGeneration(
      currentSession.generatedPrompts
    );
    
    const exportData = JSON.stringify(prompts, null, 2);
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `image-generation-${currentSession.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const togglePartitionSelection = (partitionId: string) => {
    setSelectedPartitions(prev => 
      prev.includes(partitionId) 
        ? prev.filter(id => id !== partitionId)
        : [...prev, partitionId]
    );
  };

  const renderPartitionCard = (partition: TextPartition, index: number) => {
    const prompt = currentSession?.generatedPrompts.find(p => p.partitionId === partition.id);
    const isSelected = selectedPartitions.includes(partition.id);

    return (
      <Card
        key={partition.id}
        sx={{
          height: '100%',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          cursor: 'pointer',
        }}
        onClick={() => togglePartitionSelection(partition.id)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="primary">
              Section {index + 1}
            </Typography>
            <Chip
              label={`${partition.text.split(' ').length} words`}
              size="small"
            />
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2, height: 60, overflow: 'hidden' }}>
            {partition.text.substring(0, 150)}...
          </Typography>

          {partition.analysis && (
            <Box sx={{ mb: 2 }}>
              {partition.analysis.characters.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                  <Person fontSize="small" />
                  {partition.analysis.characters.map(char => (
                    <Chip key={char} label={char} size="small" />
                  ))}
                </Box>
              )}
              
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Setting:</strong> {partition.analysis.setting}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Mood:</strong> {partition.analysis.mood}
              </Typography>
            </Box>
          )}

          {prompt && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>
                {prompt.imagePrompt.substring(0, 100)}...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 0.5, sm: 1 }, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
            flexWrap: 'wrap'
          }}
        >
          <AutoStories sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Story Prompt Studio' : 'Story Prompt Studio'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 0.5, sm: 0 }
          }}
        >
          {isMobile 
            ? 'Transform stories, poems, and songs into coherent visual prompts with intelligent text partitioning'
            : 'Transform stories, poems, and songs into coherent visual prompts with intelligent text partitioning'}
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: { xs: 1, sm: 2 },
          overflow: 'hidden',
          width: '100%'
        }}
      >
        <Box sx={{ 
          p: { xs: 1.5, sm: 3 },
          width: '100%',
          overflow: 'hidden'
        }}>
          {error && (
            <Alert 
              severity="error" 
              onClose={() => setError(null)} 
              sx={{ 
                mb: 2, 
                borderRadius: 1.5,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {error}
            </Alert>
          )}

          <Tabs 
            value={activeTab} 
            onChange={(_, v) => setActiveTab(v)}
            sx={{ 
              mb: 2,
              minHeight: { xs: 44, sm: 48 },
              '& .MuiTab-root': {
                minHeight: { xs: 44, sm: 48 },
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                px: { xs: 1, sm: 2 }
              }
            }}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
          >
            <Tab label={isMobile ? 'New' : 'New Session'} />
            <Tab label={isMobile ? 'Current' : 'Current Session'} disabled={!currentSession} />
            <Tab label={isMobile ? 'Previous' : 'Previous Sessions'} />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                startIcon={!isMobile && <Add />}
                onClick={() => setActiveStep(0)}
                fullWidth={isMobile}
                sx={{
                  py: { xs: 1.25, sm: 1.5 },
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textTransform: 'none',
                  minHeight: 48
                }}
              >
                {isMobile ? 'Start New Story Session' : 'Start New Story Session'}
              </Button>
            </Box>
            
            <Stepper 
              activeStep={activeStep} 
              orientation="vertical"
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                },
                '& .MuiStepContent-root': {
                  paddingLeft: { xs: '20px', sm: '36px' }
                }
              }}
            >
              <Step>
                <StepLabel>Input Text</StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <ToggleButtonGroup
                      value={inputMethod}
                      exclusive
                      onChange={(_, v) => v && setInputMethod(v)}
                      sx={{ 
                        mb: 2,
                        width: '100%',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: { xs: 0.5, sm: 0 },
                        '& .MuiToggleButton-root': {
                          flex: { xs: '1 1 auto', sm: '0 1 auto' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 },
                          minHeight: 44,
                          '& .MuiSvgIcon-root': {
                            fontSize: { xs: 18, sm: 20 },
                            marginRight: { xs: 0.5, sm: 1 }
                          }
                        }
                      }}
                      orientation={isMobile ? 'vertical' : 'horizontal'}
                      fullWidth={isMobile}
                    >
                      <ToggleButton value="paste">
                        <ContentPaste sx={{ mr: { xs: 0.5, sm: 1 } }} /> 
                        {isMobile ? 'Paste' : 'Paste Text'}
                      </ToggleButton>
                      <ToggleButton value="file">
                        <CloudUpload sx={{ mr: { xs: 0.5, sm: 1 } }} /> 
                        {isMobile ? 'Upload' : 'Upload File'}
                      </ToggleButton>
                      <ToggleButton value="url">
                        <LinkIcon sx={{ mr: { xs: 0.5, sm: 1 } }} /> 
                        {isMobile ? 'URL' : 'From URL'}
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {inputMethod === 'paste' && (
                      <TextField
                        fullWidth
                        multiline
                        rows={isMobile ? 6 : 8}
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder={isMobile ? "Paste your story, poem, or song here..." : "Paste your story, poem, or song here..."}
                        variant="outlined"
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />
                    )}

                    {inputMethod === 'file' && (
                      <Box>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".txt,.md"
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<CloudUpload />}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Choose File
                        </Button>
                        {inputText && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            File loaded: {inputText.split(' ').length} words
                          </Typography>
                        )}
                      </Box>
                    )}

                    {inputMethod === 'url' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          value={inputUrl}
                          onChange={(e) => setInputUrl(e.target.value)}
                          placeholder="Enter URL..."
                          variant="outlined"
                        />
                        <Button
                          variant="contained"
                          onClick={handleUrlFetch}
                          disabled={!inputUrl || isProcessing}
                        >
                          Fetch
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={createSession}
                      disabled={!inputText}
                      fullWidth={isMobile}
                      sx={{
                        minHeight: 44,
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      Continue
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>Configure Partitioning</StepLabel>
                <StepContent>
                  <Grid 
                    container 
                    spacing={2}
                    sx={{
                      width: '100%',
                      margin: 0,
                      '& > .MuiGrid-item': {
                        paddingLeft: '8px'
                      }
                    }}
                  >
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Partition Strategy</InputLabel>
                        <Select
                          value={partitionStrategy}
                          onChange={(e) => setPartitionStrategy(e.target.value as PartitionStrategy)}
                          label="Partition Strategy"
                        >
                          <MenuItem value="auto">Auto-detect scenes</MenuItem>
                          <MenuItem value="scene-based">Scene-based</MenuItem>
                          <MenuItem value="fixed-size">Fixed size</MenuItem>
                          <MenuItem value="paragraph">By paragraph</MenuItem>
                          <MenuItem value="emotional-shifts">Emotional shifts</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Target Partitions"
                        value={targetPartitions}
                        onChange={(e) => setTargetPartitions(parseInt(e.target.value))}
                        disabled={partitionStrategy !== 'auto'}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Detect Elements
                      </Typography>
                      <FormGroup row>
                        {Object.entries(detectElements).map(([key, value]) => (
                          <FormControlLabel
                            key={key}
                            control={
                              <Checkbox
                                checked={value}
                                onChange={(e) => setDetectElements({
                                  ...detectElements,
                                  [key]: e.target.checked,
                                })}
                              />
                            }
                            label={key.charAt(0).toUpperCase() + key.slice(1)}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={analyzeAndPartition}
                      disabled={!currentSession || isProcessing}
                      startIcon={isProcessing ? <CircularProgress size={20} /> : <Analytics />}
                    >
                      Analyze & Partition
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>Configure Visual Style</StepLabel>
                <StepContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Art Style</InputLabel>
                        <Select
                          value={visualStyle.artStyle}
                          onChange={(e) => setVisualStyle({
                            ...visualStyle,
                            artStyle: e.target.value as ArtStyle,
                          })}
                          label="Art Style"
                        >
                          {Object.entries(ART_STYLES).map(([key, label]) => (
                            <MenuItem key={key} value={key}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Consistency Level</InputLabel>
                        <Select
                          value={visualStyle.consistency}
                          onChange={(e) => setVisualStyle({
                            ...visualStyle,
                            consistency: e.target.value as ConsistencyLevel,
                          })}
                          label="Consistency Level"
                        >
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="low">Low</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Color Palette</InputLabel>
                        <Select
                          value={visualStyle.colorPalette}
                          onChange={(e) => setVisualStyle({
                            ...visualStyle,
                            colorPalette: e.target.value as ColorPaletteMode,
                          })}
                          label="Color Palette"
                        >
                          <MenuItem value="extracted">Extract from text</MenuItem>
                          <MenuItem value="mood-based">Based on mood</MenuItem>
                          <MenuItem value="custom">Custom</MenuItem>
                          <MenuItem value="story-progression">Story progression</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Aspect Ratio"
                        value={visualStyle.aspectRatio}
                        onChange={(e) => setVisualStyle({
                          ...visualStyle,
                          aspectRatio: e.target.value,
                        })}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Artistic Influence (optional)"
                        value={visualStyle.artisticInfluence || ''}
                        onChange={(e) => setVisualStyle({
                          ...visualStyle,
                          artisticInfluence: e.target.value,
                        })}
                        placeholder="e.g., Studio Ghibli, Monet, Art Nouveau"
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={generatePrompts}
                      disabled={!currentSession || currentSession.partitions.length === 0 || isProcessing}
                      startIcon={isProcessing ? <CircularProgress size={20} /> : <Palette />}
                    >
                      Generate Visual Prompts
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              <Step>
                <StepLabel>Select Helper Agents</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Choose agents to help iterate and improve your prompts
                  </Typography>
                  
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {Object.entries(AGENT_CATEGORIES).map(([category, agents]) => (
                      <Accordion key={category} defaultExpanded={category === 'Core Visual'}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1">{category}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {agents.map(agent => (
                              <ListItem key={agent.id}>
                                <ListItemIcon>{agent.icon}</ListItemIcon>
                                <ListItemText 
                                  primary={agent.name} 
                                  secondary={agent.description}
                                />
                                <ListItemSecondaryAction>
                                  <Checkbox
                                    checked={selectedAgents.includes(agent.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedAgents([...selectedAgents, agent.id]);
                                      } else {
                                        setSelectedAgents(selectedAgents.filter(id => id !== agent.id));
                                      }
                                    }}
                                  />
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={() => setActiveTab(1)}
                    >
                      View Results
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {currentSession && (
              <Box>
                {/* Title Row */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" sx={{ mb: 1 }}>{currentSession.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentSession.metadata.totalWords} words | {currentSession.metadata.totalPartitions} sections
                  </Typography>
                </Box>

                {/* Action Buttons Row */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<Analytics />} 
                    onClick={checkConsistency}
                    sx={{ borderRadius: 1.5 }}
                  >
                    Check Consistency
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<Download />} 
                    onClick={exportPromptsAsMarkdown}
                    sx={{ borderRadius: 1.5 }}
                  >
                    Export Markdown
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<Image />} 
                    onClick={exportForImageGeneration}
                    sx={{ borderRadius: 1.5 }}
                  >
                    Export for Generation
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<Save />} 
                    onClick={exportSession}
                    sx={{ borderRadius: 1.5 }}
                  >
                    Save Session
                  </Button>
                </Box>

                {selectedPartitions.length > 0 && (
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.selected' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography>
                        {selectedPartitions.length} sections selected
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<Merge />}
                          onClick={mergePartitions}
                          disabled={selectedPartitions.length < 2}
                        >
                          Merge
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<TipsAndUpdates />}
                          onClick={() => setIterationDialogOpen(true)}
                        >
                          Iterate with AI Agents
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                )}

                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, v) => v && setViewMode(v)}
                    size="small"
                  >
                    <ToggleButton value="grid">
                      <ViewModule />
                    </ToggleButton>
                    <ToggleButton value="list">
                      <ViewList />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {viewMode === 'grid' ? (
                  <Grid container spacing={2}>
                    {currentSession.partitions.map((partition, index) => (
                      <Grid item xs={12} md={6} lg={4} key={partition.id}>
                        {renderPartitionCard(partition, index)}
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <List>
                    {currentSession.partitions.map((partition, index) => {
                      const prompt = currentSession.generatedPrompts.find(p => p.partitionId === partition.id);
                      const isSelected = selectedPartitions.includes(partition.id);

                      return (
                        <Fragment key={partition.id}>
                          <ListItem
                            onClick={() => togglePartitionSelection(partition.id)}
                            sx={{
                              bgcolor: isSelected ? 'action.selected' : 'transparent',
                              cursor: 'pointer',
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="subtitle1">Section {index + 1}</Typography>
                                  <Chip label={`${partition.text.split(' ').length} words`} size="small" />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    {partition.text}
                                  </Typography>
                                  {prompt && (
                                    <>
                                      <Divider sx={{ my: 1 }} />
                                      <Typography variant="body2" color="primary">
                                        <strong>Prompt:</strong> {prompt.imagePrompt}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          <Divider />
                        </Fragment>
                      );
                    })}
                  </List>
                )}
              </Box>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={2}>
              {sessions.map((session) => (
                <Grid item xs={12} md={6} lg={4} key={session.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {session.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {session.metadata.totalWords} words | {session.metadata.totalPartitions} sections
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Created: {new Date(session.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Prompts: {session.generatedPrompts.length}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => {
                          setCurrentSession(session);
                          setActiveTab(1);
                        }}
                      >
                        Open
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => {
                          storyPromptStudioService.deleteSession(session.id);
                          loadSessions();
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* Iteration Dialog */}
      <Dialog
        open={iterationDialogOpen}
        onClose={() => setIterationDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <TipsAndUpdates />
            Iterate Prompts with AI Agents
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              You have selected {selectedPartitions.length} section(s). Choose how you want to improve these prompts.
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Iteration Type</InputLabel>
              <Select
                value={selectedIterationType}
                onChange={(e) => setSelectedIterationType(e.target.value)}
                label="Iteration Type"
              >
                <MenuItem value="enhance">Enhance - Add more detail and richness</MenuItem>
                <MenuItem value="simplify">Simplify - Make more concise and clear</MenuItem>
                <MenuItem value="stylize">Stylize - Apply specific artistic style</MenuItem>
                <MenuItem value="consistency">Consistency - Ensure uniform style across prompts</MenuItem>
                <MenuItem value="cinematic">Cinematic - Add film-like qualities</MenuItem>
                <MenuItem value="atmospheric">Atmospheric - Enhance mood and ambiance</MenuItem>
                <MenuItem value="technical">Technical - Add precise technical details</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mb: 2 }}>Select AI Agent</Typography>
            
            <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 3 }}>
              {Object.entries(AGENT_CATEGORIES).map(([category, agents]) => (
                <Accordion key={category} defaultExpanded={category === 'Core Visual'}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2">{category}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={selectedIterationAgent}
                        onChange={(e) => setSelectedIterationAgent(e.target.value)}
                      >
                        {agents.map(agent => (
                          <FormControlLabel
                            key={agent.id}
                            value={agent.id}
                            control={<Radio />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {agent.icon}
                                <Box>
                                  <Typography variant="body2">{agent.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {agent.description}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Instructions (Optional)"
              value={iterationFeedback}
              onChange={(e) => setIterationFeedback(e.target.value)}
              placeholder="Provide specific guidance for the AI agent..."
              helperText="Give the agent specific instructions about what you want to achieve"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setIterationDialogOpen(false)}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => iteratePrompts()}
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={20} /> : <PlayArrow />}
            sx={{
              borderRadius: 1.5,
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {isProcessing ? 'Processing...' : 'Iterate Prompts'}
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </Paper>
    </Container>
  );
};